import "twin.macro";
import tw from "twin.macro";

import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Link } from "react-router";

import { Button, Checkbox, Chip, IconButton, Tooltip } from "@mui/material";

import _ from "lodash";

import { OpaqueCard } from "@components/StyledComponents";
import { BlockCard, FaIcon } from "@features/ui";
import { BlockCardActions, BlockCardNoContent } from "@features/ui/BlockCard";
import IndexTable from "@features/ui/indexTable";
import { insertIf } from "@features/ui/indexTable/helpers";
import { useRemoveMultiOrgUserFromOrgMutation } from "@features/users/data";
import { useAddDistributorUserMutation } from "@features/users/data/distributorUserQueries";
import { OptimizedOrganization } from "@models/Organization";
import { User } from "@models/User";
import { useSetLocation } from "@services/reactRouterDom";
import useRoleIs from "@utils/useRoleIs";

import BulkEditUsersModal from "./BulkEditUsersModal";
import { FormData } from "./DistributorForm";
import DistributorUserEntitySelector, {
  DistributorUserEntitySelectorProps,
} from "./DistributorUserEntitySelector";

const distOrganizationFieldName = (
  id: string
): `orderers.${string}.distributorOrganizationIds` =>
  `orderers.${id}.distributorOrganizationIds`;

const useUserHasAccessToOrg = (user: User, org: OptimizedOrganization) => {
  const userOrgs: number[] =
    useWatch({ name: distOrganizationFieldName(user.id) }) ?? [];
  return userOrgs.includes(+org.id);
};

const AccessToOrgToggle = ({
  user,
  org,
}: {
  user: User;
  org: OptimizedOrganization;
}) => {
  const roleIs = useRoleIs();
  const hasAccess = useUserHasAccessToOrg(user, org);

  const { setValue, getValues } = useFormContext();
  const { mutate: addDistributorUser, isPending: isAdding } =
    useAddDistributorUserMutation();
  const { mutate: removeUser, isPending: isRemoving } =
    useRemoveMultiOrgUserFromOrgMutation();
  const isPending = isAdding || isRemoving;

  const toggleFormUserAccess = () => {
    const name = distOrganizationFieldName(user.id);
    setValue(name, _.xor(getValues(name), [+org.id]), { shouldDirty: true });
  };

  const handleToggle = () => {
    if (roleIs("super")) {
      toggleFormUserAccess();
    } else {
      if (hasAccess) {
        removeUser(user, {
          onSuccess: () => toggleFormUserAccess(),
        });
      } else {
        addDistributorUser(user.id, {
          onSuccess: () => toggleFormUserAccess(),
        });
      }
    }
  };

  return (
    <Tooltip
      placement="left"
      title={
        isPending
          ? "Saving..."
          : hasAccess
            ? "Remove access to this organization"
            : "Add access to this organization"
      }
    >
      <span>
        <IconButton
          tw="-my-1"
          css={hasAccess && tw`bg-green-50 hover:bg-green-100`}
          size="large"
          onClick={handleToggle}
          disabled={isPending}
        >
          <FaIcon
            icon={hasAccess ? "check-circle" : "plus"}
            tw="aspect-square text-lg"
            css={hasAccess ? tw`text-green-500` : tw`text-primary-700`}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};

// Wrapper to disable the selector if the user doesn't have access to the org
const UserEntitySelector = ({
  user,
  org,
  ...props
}: DistributorUserEntitySelectorProps & {
  user: User;
  org: OptimizedOrganization;
}) => {
  const hasAccess = useUserHasAccessToOrg(user, org);
  return <DistributorUserEntitySelector {...props} disabled={!hasAccess} />;
};

const UserCount = ({
  distributorOrg,
  activeUsers,
}: {
  distributorOrg: OptimizedOrganization;
  activeUsers: User[];
}) => {
  const total = activeUsers.length;
  const orderers: FormData["orderers"] = useWatch({ name: "orderers" });
  if (total === 0) return null;
  const selected = activeUsers.filter((user) =>
    orderers[user.id]?.distributorOrganizationIds.includes(+distributorOrg.id)
  ).length;
  return `${selected} of ${total} users have access`;
};

const DistributorUsersForm = ({
  distributorOrg,
  orderers,
}: {
  distributorOrg: OptimizedOrganization;
  orderers: User[];
}) => {
  const isSuper = useRoleIs()("super");
  const setLocation = useSetLocation();
  const { setValue, getValues } = useFormContext<FormData>();
  const [showInactive, setShowInactive] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const orderersWithAccess = orderers.map((user) => ({
    ...user,
    hasAccess: user.distributorOrganizationIds.includes(+distributorOrg.id),
  }));
  const activeUsers = orderersWithAccess.filter(
    (user) => user.status === "active"
  );
  const usersToShow = _.sortBy(
    showInactive ? orderersWithAccess : activeUsers,
    "status",
    "name"
  );

  const handleToggleSelection = (userId: string) => {
    setSelectedUserIds((prev) => _.xor(prev, [userId]));
  };

  const handleSelectAll = () => {
    const selectableUserIds = usersToShow
      .filter((user) => isSuper || user.hasAccess)
      .map((user) => user.id);

    if (selectedUserIds.length === selectableUserIds.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(selectableUserIds);
    }
  };

  const handleGiveAccessToAll = () => {
    selectedUserIds.forEach((userId) => {
      setValue(
        distOrganizationFieldName(userId),
        _.uniq(
          getValues(distOrganizationFieldName(userId)).concat(
            +distributorOrg.id
          )
        ),
        { shouldDirty: true }
      );
    });
  };

  type Row = (typeof orderersWithAccess)[number];
  return (
    <BlockCard
      key={distributorOrg.id}
      title="Orderers"
      secondary={
        <UserCount distributorOrg={distributorOrg} activeUsers={activeUsers} />
      }
    >
      {orderers.length === 0 && (
        <BlockCardNoContent>No orderers yet</BlockCardNoContent>
      )}
      {orderers.length > 0 && (
        <IndexTable
          tw="-mx-6"
          columns={[
            {
              id: "_bulkSelect",
              label: (
                <Checkbox
                  size="small"
                  tw="-mx-2"
                  checked={
                    selectedUserIds.length > 0 &&
                    selectedUserIds.length ===
                      usersToShow.filter((user) => isSuper || user.hasAccess)
                        .length
                  }
                  indeterminate={
                    selectedUserIds.length > 0 &&
                    selectedUserIds.length <
                      usersToShow.filter((user) => isSuper || user.hasAccess)
                        .length
                  }
                  onChange={handleSelectAll}
                  disabled={
                    usersToShow.filter((user) => isSuper || user.hasAccess)
                      .length === 0
                  }
                />
              ),
              render: (_, user) => (
                <Checkbox
                  size="small"
                  tw="-mx-2"
                  checked={selectedUserIds.includes(user.id)}
                  onChange={() => handleToggleSelection(user.id)}
                  disabled={!(isSuper || user.hasAccess)}
                  onClick={(e) => e.stopPropagation()}
                />
              ),
            },
            {
              id: "_actions",
              label: "Access",
              render: (_, user) => (
                <AccessToOrgToggle user={user} org={distributorOrg} />
              ),
            },
            {
              id: "name",
              label: "Name",
              className: "leading-8 whitespace-nowrap",
              render: (name, { hasAccess }) =>
                isSuper || hasAccess ? (
                  name
                ) : (
                  <span tw="text-neutral-400">{name}</span>
                ),
            },
            {
              id: "email",
              label: "Email",
              className: "leading-8 whitespace-nowrap",
              render: (email, { id, hasAccess }) =>
                isSuper || hasAccess ? (
                  <Link
                    to={`/admin/users/${id}`}
                    tw="flex items-center gap-2 hover:underline"
                  >
                    {email} <FaIcon icon="external-link-alt" tw="text-xs" />
                  </Link>
                ) : (
                  <span tw="text-neutral-400">{email}</span>
                ),
            },
            ...insertIf(showInactive, {
              id: "status",
              label: "Status",
              render: (status) => (
                <Chip
                  label={_.startCase(status)}
                  color={status === "active" ? "success" : "default"}
                />
              ),
            }),
            {
              id: "limitedBrands",
              label: "Brands",
              render: (_, user) => (
                <UserEntitySelector
                  user={user}
                  org={distributorOrg}
                  distFieldName={`distributorOrganizations.${distributorOrg.id}.brandIds`}
                  userFieldName={`orderers.${user.id}.limitedBrands`}
                  label="brands"
                  orgOptions={distributorOrg.brands}
                  limitTags={2}
                />
              ),
            },
            ...insertIf<Row>(distributorOrg.channels.length > 0, {
              id: "channels",
              label: "Channels",
              render: (_, user) => (
                <UserEntitySelector
                  user={user}
                  org={distributorOrg}
                  distFieldName={`distributorOrganizations.${distributorOrg.id}.channelIds`}
                  userFieldName={`orderers.${user.id}.channelIds`}
                  label="channels"
                  orgOptions={distributorOrg.channels}
                  limitTags={2}
                />
              ),
            }),
            {
              id: "territories",
              label: "Territories",
              render: (_, user) => (
                <UserEntitySelector
                  user={user}
                  org={distributorOrg}
                  distFieldName={`distributorOrganizations.${distributorOrg.id}.territoryIds`}
                  userFieldName={`orderers.${user.id}.territoryIds`}
                  label="territories"
                  orgOptions={distributorOrg.territories}
                />
              ),
            },
          ]}
          rows={usersToShow}
          noResultsText={
            "No active users. Click 'Show Inactive' to see all users."
          }
        />
      )}
      <BlockCardActions>
        {activeUsers.length !== orderers.length && (
          <Button variant="text" onClick={() => setShowInactive(!showInactive)}>
            {showInactive ? "Hide Inactive" : "Show Inactive"}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={() => setLocation({ hash: "#new-user" }, { replace: true })}
        >
          Create User
        </Button>
      </BlockCardActions>

      {/* Floating Action Bar */}
      {selectedUserIds.length > 0 && (
        <OpaqueCard tw="fixed left-1/2 -translate-x-1/2 bottom-8 px-6 z-10 drop-shadow-lg border-2 border-primary-900">
          <div tw="flex items-baseline gap-6">
            <div tw="text-neutral-700">
              {selectedUserIds.length} user
              {selectedUserIds.length !== 1 ? "s" : ""} selected
            </div>
            <div tw="flex gap-2">
              <Button
                variant="outlined"
                size="small"
                onClick={handleGiveAccessToAll}
              >
                Grant Access
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setBulkEditModalOpen(true)}
              >
                Bulk Edit
              </Button>
            </div>
          </div>
        </OpaqueCard>
      )}

      {/* Bulk Edit Modal */}
      {bulkEditModalOpen && (
        <BulkEditUsersModal
          open={bulkEditModalOpen}
          onClose={() => {
            setBulkEditModalOpen(false);
            setSelectedUserIds([]);
          }}
          selectedUserIds={selectedUserIds}
          distributorOrg={distributorOrg}
        />
      )}
    </BlockCard>
  );
};

export default DistributorUsersForm;
